import { css } from "simplydo/react/styled";
import { Icon } from "simplydo/react/ui/icon";

import { Label, LabelLink } from "../label";
import { CONTROL_HEIGHT, SIZE_FONT } from "../utils/colors";

/** Shared chrome + modifier classes for all button look variants. */
export const buttonBaseClass = css`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: none;
  border: none;
  vertical-align: middle;
  margin: 0 0.45em 0 0;
  padding: 0 1.5em;
  text-transform: none;
  text-shadow: none;
  font-weight: bold;
  line-height: 1em;
  font-style: normal;
  text-align: center;
  text-decoration: none;
  border-radius: 0.3em;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition:
    opacity 0.2s ease,
    background-color 0.1s ease,
    color 0.1s ease,
    box-shadow 0.1s ease;
  box-sizing: border-box;
  font-family: inherit;
  position: relative;
  font-size: 1rem;
  white-space: nowrap;

  &:focus-visible {
    outline: auto 2px Highlight;
    outline: auto 2px -webkit-focus-ring-color;
  }

  &:disabled,
  &.is-disabled {
    cursor: default;
    opacity: 0.45 !important;
    background-image: none !important;
    pointer-events: none;
  }

  &.is-small {
    font-size: ${SIZE_FONT.small};
    height: ${CONTROL_HEIGHT.small};
  }
  &.is-medium {
    font-size: ${SIZE_FONT.medium};
    height: ${CONTROL_HEIGHT.medium};
  }
  &.is-large {
    font-size: ${SIZE_FONT.large};
    height: ${CONTROL_HEIGHT.large};
  }

  &:last-child {
    margin-right: 0;
  }

  &.is-fluid {
    display: flex;
    width: 100%;

    ~ button:last-child {
      margin: 0;
    }

    &:not(:has(+ button)) {
      margin: 0;
    }
  }

  &.is-icon {
    width: 1em;
    padding: 0;
  }

  &.is-small.is-icon {
    width: ${CONTROL_HEIGHT.small};
    min-width: ${CONTROL_HEIGHT.small};
  }

  &.is-medium.is-icon {
    width: ${CONTROL_HEIGHT.medium};
    min-width: ${CONTROL_HEIGHT.medium};
  }

  &.is-large.is-icon {
    width: ${CONTROL_HEIGHT.large};
    min-width: ${CONTROL_HEIGHT.large};
  }

  ${Icon} {
    opacity: 0.9;
  }

  &.is-icon > ${Icon} {
    margin: 0 !important;
    vertical-align: top;
  }

  &:not(.is-icon):has(${Icon}) {
    padding: 0 1.3em 0 1.5em;

    > ${Icon}:last-child {
      margin-right: 0;
      margin-left: 0.6em;
    }

    &.is-icon-left {
      padding: 0 1.5em 0 1.3em;

      > ${Icon}:first-child {
        margin-left: 0;
        margin-right: 0.6em;
      }
    }
  }

  &:has(${Label}.is-small:last-child),
  &:has(${LabelLink}.is-small:last-child) {
    padding: 0 1.5em;
  }

  &:has(${Label}.is-medium:last-child),
  &:has(${LabelLink}.is-medium:last-child) {
    padding: 0 1.5em;
  }

  > ${Label}:last-child,
  > ${LabelLink}:last-child {
    margin-left: 0.7em;
  }

  &.is-loading {
    color: transparent !important;
    pointer-events: none;
  }

  &.is-loading::before {
    position: absolute;
    content: "";
    top: 50%;
    left: 50%;
    margin: -0.64285714em 0 0 -0.64285714em;
    width: 1.28571429em;
    height: 1.28571429em;
    border-radius: 500rem;
    border: 0.2em solid rgba(0, 0, 0, 0.1);
  }

  &.is-loading::after {
    position: absolute;
    content: "";
    top: 50%;
    left: 50%;
    margin: -0.64285714em 0 0 -0.64285714em;
    width: 1.28571429em;
    height: 1.28571429em;
    border-radius: 500rem;
    border-color: #767676 transparent transparent;
    border-style: solid;
    border-width: 0.2em;
    animation: sdo-button-spin 0.6s linear;
    animation-iteration-count: infinite;
  }

  @keyframes sdo-button-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
